export async function registerSw() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers not supported");
  }
  const reg = await navigator.serviceWorker.register("/sw.js");
  return reg;
}

/**
 * Request notification permission from the user.
 * Call after SW is registered. Best triggered by a user action (e.g. button click) for better UX.
 * @returns {"granted" | "denied" | "default"}
 */
export async function requestNotificationPermission() {
  if (!("Notification" in window)) {
    console.warn("This browser does not support notifications");
    return "denied";
  }
  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Convert a VAPID public key (base64url or base64) to Uint8Array for pushManager.subscribe().
 * @param {string} base64String - VAPID public key from your backend
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

/**
 * Subscribe for push notifications. Requires:
 * - Service worker registered and activated
 * - Notification permission granted
 * - VAPID public key from your backend
 *
 * The subscription object is created by the browser (not by us). It looks like:
 *   { endpoint: "https://fcm.googleapis.com/...", keys: { p256dh: "...", auth: "..." } }
 * Chrome uses FCM as its push gateway, so the endpoint may be fcm.googleapis.com;
 * other browsers use other gateways. We don't care which—store { endpoint, keys }
 * on the backend and use the web-push library to send; it handles the rest.
 *
 * @param {string} vapidPublicKey - VAPID public key (base64url string)
 * @returns {Promise<PushSubscription|null>} - Send this to your backend (e.g. subscription.toJSON()); backend stores it and uses web-push to send.
 */
export async function subscribeForPush(vapidPublicKey) {
  if (!vapidPublicKey || typeof vapidPublicKey !== "string") {
    console.warn("subscribeForPush: VAPID public key is required");
    return null;
  }
  if (!("PushManager" in window)) {
    console.warn("Push messages are not supported");
    return null;
  }
  const reg = await navigator.serviceWorker.ready;
  if (!reg.pushManager) {
    console.warn("PushManager not available");
    return null;
  }
  const permission = Notification.permission;
  if (permission !== "granted") {
    console.warn("Notification permission not granted:", permission);
    return null;
  }
  try {
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
    return subscription;
  } catch (err) {
    console.warn("Push subscription skipped:", err);
    return null;
  }
}
